import Heading from '@/Components/Heading';
import { PageType } from '@/Components/HeroBanner';
import DetailsHero from '@/Components/HeroBanner/DetailsHero';
import ShowHeroBanner from '@/Components/HeroBanner/ShowHeroBanner';
import ErrorMessage from '@/Components/Message';
import Synopsis from '@/Components/Synopsis';
import synopsisStyles from '@/Components/Synopsis/styles.module.scss';
import usePackageDetails from '@/Hooks/PUJ/usePackageDetail';
import useBrandDetails from '@/Hooks/useBrandDetails';
import useMedia from '@/Hooks/useMedia';
import usePujFlag from '@/Hooks/usePujFlag';
import { useContentSubscriptionCheck } from '@/Hooks/useSubscription';
import Content from '@/Layouts/containers/Content';
import Preloader from '@/Layouts/containers/Preloader';
import Seasons from '@/Pages/Detail/Seasons';
import Soundtrack from '@/Pages/Detail/Soundtrack';
import WhatsOnTv from '@/Pages/Detail/WhatsOnTv';
import { actions, selectors, utils } from '@/Store';
import { shouldRefetchForPackageChange } from '@/Utils/Puj';
import { withTransaction } from '@elastic/apm-rum-react';
import { BrandType } from '@skytvnz/sky-app-store/lib/types/enums/BrandType';
import { isEmpty, isNil } from 'ramda';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePrevious } from 'react-use';
import usePinValidationModal from '@/Components/Modal/usePinValidationModal';
import Episodes from './Seasons/Episodes';
import styles from './styles.module.scss';

const ShowDetail: React.FC = () => {
  const {
    brand: show,
    isSport,
    image,
    imageType,
    selectedSeason,
    brandId,
    brandIsLoading,
    onMyListToggle,
    highlights,
    onHighlightsPaginate,
    refetch,
  } = useBrandDetails('ShowDetail');
  const dispatch = useDispatch();

  const { hasVodOrLinearPkgUpgrade } = useContentSubscriptionCheck();
  const hasPkgUpgrade = hasVodOrLinearPkgUpgrade(show);

  const { isPUJEnabled } = usePujFlag();
  const { isMediaM } = useMedia();
  const {
    offerText,
    discountPrice,
    packageTitle,
    basePrice,
    hintMessage,
    offerId,
    collectionId,
    collectionTitle,
    actualPrice,
  } = usePackageDetails(show, show?.viewingMethods, false);
  const packageDetails = {
    isPackageDetailPage: false,
    packagePrice: basePrice,
    packageTitle,
    discountPrice,
    offerText,
    offerId,
    hintMessage,
    collectionId,
    collectionTitle,
    actualPrice,
  };
  const customerPackagesIds = useSelector(selectors.customer.customerPackagesIds);
  const previousCustomerPackagesIds = usePrevious(customerPackagesIds);
  useEffect(() => {
    if (customerPackagesIds && previousCustomerPackagesIds) {
      if (shouldRefetchForPackageChange(show, customerPackagesIds, previousCustomerPackagesIds)) {
        refetch();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerPackagesIds]);

  const selectedSeasonChanged = season => {
    dispatch(actions.titles.selectSeason(brandId, season.id));
  };
  const hasEpisodes =
    show?.__typename === BrandType.Show && !isNil(show?.seasons) && !isEmpty(show.seasons);

  const highlightsContent = utils.pagination.getContent(highlights);
  const hasHighlights = !isNil(highlightsContent) && !isEmpty(highlightsContent);
  const isHome = false;
  const { openPinModal } = usePinValidationModal();

  return (
    <Preloader isLoading={isNil(show) && brandIsLoading}>
      {!isNil(show) && show.__typename === BrandType.Show ? (
        <>
          <ShowHeroBanner
            className={styles.heroBanner}
            show={show}
            image={image}
            imageType={imageType}
            pageType={PageType.Detail}
            onMyListToggle={onMyListToggle}
            packageDetails={packageDetails}
            openPinModal={openPinModal}
          />
          {!isMediaM && (
            <Content className={synopsisStyles.synopsis}>
              <Synopsis
                synopsis={show.synopsis}
                cast={show.defaultEpisode?.characters}
                hasPkgUpgrade={hasPkgUpgrade}
              />
              {isPUJEnabled && (
                <DetailsHero
                  brand={show}
                  isHome={isHome}
                  onMyListToggle={onMyListToggle}
                  hasPkgUpgrade={hasPkgUpgrade}
                  packageDetails={packageDetails}
                  openPinModal={openPinModal}
                />
              )}
            </Content>
          )}
          {/* <Trailers />*/}
          <WhatsOnTv brand={show} />
          {hasHighlights && (
            <div data-testid="highlight">
              <Content>
                <Heading testid="highlight-heading">Mini Highlights</Heading>
              </Content>
              <Episodes
                episodes={highlightsContent}
                brandId={brandId}
                isSport={isSport}
                playbackOrigin="Content Mini Highlights"
                isTilesLoading={utils.pagination.isLoading(highlights)}
                hasError={utils.pagination.isLoadingError(highlights)}
                hasNextPage={utils.pagination.hasNextPage(highlights)}
                tilesLoadHandler={onHighlightsPaginate}
              />
            </div>
          )}
          {hasEpisodes && (
            <Seasons
              seasons={show.seasons}
              selectedSeason={selectedSeason}
              brandId={brandId}
              onSeasonChange={selectedSeasonChanged}
              isSport={isSport}
            />
          )}

          <Soundtrack selectedSeason={selectedSeason} brandId={show.id} />
          {/* <Similar />*/}
        </>
      ) : (
        <ErrorMessage />
      )}
    </Preloader>
  );
};

export default withTransaction('ShowDetail', 'component')(ShowDetail);
